import { Injectable } from "@angular/core";
import { HttpClient } from "@angular/common/http";
import { firstValueFrom, Observable, zip } from "rxjs";
import { AssetResource, MediaFile, Resources } from "@rvi/asset-modules";
import { AssetModuleClient } from "../asset-module/asset-module-client.service";
import { StructureContractService } from "../interfaces/structure-contract.service";
import JSZip from "jszip";
import { SubjectModelDbService } from "../database/subject-model-db.service";
import { switchMap } from "rxjs/operators";
import { SubjectLocation } from "../../_objects/subject-model";
import { TitleDbService } from "../database/title-db.service";
import { InspectionDbService } from "../database/inspection-db.service";
import { TitleSchema } from "../../_offline/models/title.schema";
import { InspectionSchema } from "../../_offline/models/inspection.schema";
import { SubjectDbService } from "../database/subject-db.service";
import { OfflineUtilsService } from "../../_offline/services/offline-utils.service";
import { UtilsService } from "../_utils.service";
import { MarksStateService } from "../mark-state/marks-state.service";
import { MarkItem } from "../../_objects/mark";
import { FILE_SYSTEM } from "../../_offline/electron.constants";

interface LeafFolder {
  location: SubjectLocation;
  folder: JSZip;
}

@Injectable({
  providedIn: "root"
})
export class StructureOfflineService implements StructureContractService {
  readonly MARK_FILE_NAME = "MARK_SET.dat";
  readonly TITLE_FILE_NAME = "TITLE.dat";

  private folderIndex = 1;

  constructor(
    private inspectionDbService: InspectionDbService,
    private subjectDbService: SubjectDbService,
    private subjectModelDbService: SubjectModelDbService,
    private titleDbService: TitleDbService,
    private utilsService: UtilsService,
    private offlineUtils: OfflineUtilsService,
    private marksStateService: MarksStateService,
    private httpClient: HttpClient,
    private assetModuleClient: AssetModuleClient
  ) {}

  getStructureZip(subjectModelId?: string, inspectionId?: string, withPreset?: boolean): Observable<Blob> {
    if (subjectModelId) {
      return this.getWithSubjectModelId(subjectModelId, withPreset);
    }

    if (!inspectionId) {
      throw new Error("Missing inspection ID");
    }

    return this.getWithInspectionId(inspectionId, withPreset);
  }

  private getWithInspectionId(inspectionId: string, withPreset?: boolean) {
    let inspection: InspectionSchema;
    let marks: MarkItem[];
    let title: TitleSchema;

    return zip(this.inspectionDbService.getInspection(inspectionId, true), this.marksStateService.markItems$, this.titleDbService.getAll())
      .pipe(
        switchMap((values) => {
          inspection = values[0];
          marks = values[1];
          title = values[2];

          return this.subjectDbService
            .getOne(inspection.subjectId)
            .pipe(switchMap((subject) => this.subjectModelDbService.getOne(subject.subjectModelId!)));
        })
      )
      .pipe(
        switchMap((subjectModel) => {
          const subjectModelLocation = subjectModel.subjectModelLocations?.find((_) => _.version === inspection.locationVersion || _.isNewestVersion);
          const locations = this.parseLocations(subjectModelLocation?.locations);

          return this.buildStructureZip(inspection.title, locations, marks, title, withPreset);
        })
      );
  }

  private getWithSubjectModelId(subjectModelId: string, withPreset?: boolean) {
    return zip(this.subjectModelDbService.getOne(subjectModelId), this.marksStateService.markItems$, this.titleDbService.getAll()).pipe(
      switchMap((values) => {
        const subjectModel = values[0];
        const marks = values[1];
        const title = values[2];

        const newestLocations = subjectModel.subjectModelLocations.find((_) => _.isNewestVersion);
        const locations = this.parseLocations(newestLocations?.locations);

        return this.buildStructureZip(subjectModel.name, locations, marks, title, withPreset);
      })
    );
  }

  private async buildStructureZip(
    rootName: string,
    locations: SubjectLocation[],
    marks: MarkItem[],
    title: TitleSchema,
    withPreset?: boolean
  ): Promise<Blob> {
    this.folderIndex = 1;

    const zipFile = new JSZip();
    const rootFolder = zipFile.folder(this.utilsService.trimAllSpace(rootName));

    const leaves: LeafFolder[] = [];
    if (rootFolder !== null) {
      locations.forEach((location) => {
        this.collectLeafFolders(location, rootFolder, leaves);
      });
    }

    if (withPreset) {
      this.addPresetFolder(zipFile, marks, title);
    }

    await this.addSharedWithDeviceFiles(leaves);

    return zipFile.generateAsync<"blob">({
      type: "blob",
      platform: "UNIX",
      compression: "DEFLATE",
      compressionOptions: { level: 9 }
    });
  }

  private addPresetFolder(zipFile: JSZip, marks: MarkItem[], title: TitleSchema): void {
    const presetFolder = zipFile.folder(FILE_SYSTEM.PATH.PRESET);

    if (presetFolder !== null) {
      presetFolder.file(this.MARK_FILE_NAME, this.offlineUtils.generateMarksText(marks));
      presetFolder.file(this.TITLE_FILE_NAME, this.offlineUtils.generateTitlesText(title));
    }
  }

  private parseLocations(locationsJson?: string): SubjectLocation[] {
    return locationsJson ? JSON.parse(locationsJson) : [];
  }

  private collectLeafFolders(location: SubjectLocation, baseFolder: JSZip, acc: LeafFolder[]): void {
    if (location.children.length > 0) {
      location.children.forEach((child) => {
        this.collectLeafFolders(child, baseFolder, acc);
      });
      return;
    }

    const folderName = location.isDataPlate ? "00_" + location.identifier : String(this.folderIndex).padStart(2, "0") + "_" + location.identifier;

    if (!location.isDataPlate) {
      this.folderIndex++;
    }

    const folder = baseFolder.folder(folderName);
    if (folder !== null) {
      acc.push({ location, folder });
    }
  }

  private async addSharedWithDeviceFiles(leaves: LeafFolder[]): Promise<void> {
    await Promise.all(leaves.map((leaf) => this.addLeafResources(leaf)));
  }

  private async addLeafResources({ location, folder }: LeafFolder): Promise<void> {
    const assetTypeResources = location.assetTypeId
      ? await this.fetchResources(this.assetModuleClient.getAssetTypeResources(location.assetTypeId))
      : [];
    const liveAssetResources = location.liveAssetId
      ? await this.fetchResources(this.assetModuleClient.getLiveAssetResources(location.liveAssetId))
      : [];

    const mediaFiles = [...assetTypeResources, ...liveAssetResources]
      .filter((resource) => resource.isSharedWithDevice)
      .filter(Resources.isFileResource)
      .flatMap((resource) => resource.mediaFiles);

    await Promise.all(mediaFiles.map((mediaFile) => this.addResourceFile(folder, mediaFile)));
  }

  private async fetchResources(resources: Observable<AssetResource[]>): Promise<AssetResource[]> {
    try {
      return await firstValueFrom(resources);
    } catch (error) {
      console.error("Failed to fetch shared-with-device resources", error);
      return [];
    }
  }

  private async addResourceFile(folder: JSZip, mediaFile: MediaFile): Promise<void> {
    if (!mediaFile.fileDownloadUrl) {
      return;
    }

    try {
      const blob = await firstValueFrom(this.httpClient.get(mediaFile.fileDownloadUrl, { responseType: "blob" }));
      folder.file(mediaFile.fileName, await blob.arrayBuffer());
    } catch (error) {
      console.error(`Failed to download shared-with-device resource ${mediaFile.fileName}`, error);
    }
  }
}
